// e12: the "Ziggie host" treatment — Ziggie asks the questions and gives the
// feedback in his speech bubble. Same flow and logic as e11, except energy,
// sleep and diet now come before About you, the intro is new, and first-step,
// motivation and the no-weight-loss plan graph are gone. Images are shared with
// e4/e10 until the new e12 artwork lands.
import { QuizConfig } from './types'
import { GB_EXIT_PATH } from '../components/constants'

const CREAM = 'var(--colour-surface-cream-subtle)'

const e12: QuizConfig = {
  productType: 'membership',
  phoneTypeScale: true,
  screens: [
    // ============================================================
    // SECTION 1: Intro
    // ============================================================
    {
      path: '/e12/welcome',
      type: 'ContentScreen',
      hideProgressBar: true,
      props: {
        title: 'Better health starts with your gut',
        topImage: '/img/e12/welcome.webp',
        topImageAlt: 'Ziggie waving next to a meal scored in the ZOE app',
        // Lined up with the Ziggie in the design's hero: his eyes match to the pixel.
        topImageGif: {
          gif: 'ziggie-wave',
          still: '/img/gifs/ziggie-wave-still.webp',
          left: '52.27%',
          top: '16.35%',
          width: '55.17%',
        },
        topImageAspectRatio: '750 / 509',
        topImageBleed: true,
        tightTop: true,
        centerText: true,
        showRatingsRow: true,
        largeTitle: true,
        buttonText: 'Get started',
      },
    },
    {
      path: '/e12/goals',
      type: 'MultiSelectScreen',
      props: {
        title: 'What do you want to achieve?',
        description: 'Choose as many as you want',
        questionKey: 'goals',
        withZiggie: { gif: 'ziggie-idle' },
        options: [
          { label: 'Live longer', value: 'live_longer' },
          { label: 'Lose weight', value: 'lose_weight' },
          { label: 'Improve my gut health', value: 'improve_gut_health' },
          { label: 'Feel better', value: 'feel_better' },
          { label: 'Sleep better', value: 'sleep_better' },
        ],
      },
    },
    {
      path: '/e12/ziggie-intro',
      type: 'ZiggieTalkScreen',
      backgroundColor: CREAM,
      props: {
        gif: 'ziggie-wave',
        messages: [
          'You’ve taken the first step!{br}{br}<b>I’m Ziggie,</b> one of the trillions of microbes in your gut. Here to guide you on your journey to better health.',
        ],
      },
    },
    {
      // Figma names the key age_range; kept as welcome_age_range so the hormonal
      // showWhen and analytics match e11.
      path: '/e12/welcome-age-range',
      type: 'SelectScreen',
      props: {
        title: 'What’s your age?',
        questionKey: 'welcome_age_range',
        withZiggie: { gif: 'ziggie-idle' },
        options: [
          { label: '18–34', value: '18_34' },
          { label: '35–49', value: '35_49' },
          { label: '50–64', value: '50_64' },
          { label: '65+', value: '65_plus' },
        ],
      },
    },
    {
      // Same collage as e10/e11.
      path: '/e12/social-proof',
      type: 'SocialProofScreen',
      backgroundColor: CREAM,
      props: {
        title: 'ZOE has helped <green>{number}</green> people like you achieve their goals.',
        topImage: '/img/e10/social-proof.png',
        topImageAspectRatio: '1149 / 750',
        topImageAlt: 'ZOE members',
        topImageBleed: true,
        tightTop: true,
        topImageSpacing: 32,
        titleVariant: 'large',
        hideLaurels: true,
        centerContent: true,
        ageTokens: {
          number: {
            '18_34': '36,000+',
            '35_49': '117,000+',
            '50_64': '134,000+',
            '65_plus': '44,000+',
            unknown: '340,000+',
          },
        },
      },
    },
    {
      path: '/e12/heard-about-gut-health',
      type: 'SelectScreen',
      props: {
        title: 'Have you heard about gut health?',
        questionKey: 'heard_about_gut_health',
        withZiggie: { gif: 'ziggie-idle' },
        options: [
          { label: 'Just the name', value: 'just_the_name' },
          { label: 'I know a few things', value: 'a_few_things' },
          { label: 'I know everything about it!', value: 'everything' },
        ],
      },
    },
    {
      path: '/e12/gut-health-feedback-just-the-name',
      type: 'ZiggieTalkScreen',
      backgroundColor: CREAM,
      showWhen: { heard_about_gut_health: 'just_the_name' },
      props: {
        gif: 'ziggie-idle',
        messages: [
          '<b>You’re in good hands. </b>ZOE scientists are world leaders in gut health. I’ll bring their knowledge to you.',
        ],
      },
    },
    {
      path: '/e12/gut-health-feedback-a-few-things',
      type: 'ZiggieTalkScreen',
      backgroundColor: CREAM,
      showWhen: { heard_about_gut_health: 'a_few_things' },
      props: {
        gif: 'ziggie-idle',
        messages: [
          '<b>Nice start.</b> ZOE scientists are world leaders in gut health. I’ll build on what you already know.',
        ],
      },
    },
    {
      path: '/e12/gut-health-feedback-everything',
      type: 'ZiggieTalkScreen',
      backgroundColor: CREAM,
      showWhen: { heard_about_gut_health: 'everything' },
      props: {
        gif: 'ziggie-idle',
        messages: [
          '<b>You know your stuff.</b> ZOE scientists are world leaders in gut health. We’ll go deeper into their science.',
        ],
      },
    },
    {
      path: '/e12/consent',
      type: 'MandatoryConsentScreen',
      backgroundColor: CREAM,
      props: {
        title: 'Your body, your data',
        largeTitle: true,
        description:
          'We need your consent to use your diet and health data to personalise your quiz and onboarding experience.',
        points: ['You can withdraw consent at any time', 'Learn more in our {privacyPolicy}'],
        topImage: '/img/e12/consent-scientists.webp',
        topImageAlt: 'ZOE scientists',
        topImageAspectRatio: '800 / 513',
        hideCheckbox: true,
        consentQuestionKey: 'data_consent',
        buttonText: 'I consent',
      },
    },

    // ============================================================
    // SECTION 2: Energy and sleep
    // ============================================================
    {
      path: '/e12/lifestyle-intro',
      type: 'ZiggieTalkScreen',
      backgroundColor: CREAM,
      props: {
        gif: 'ziggie-idle',
        messages: [
          '<b>You’re all set.</b> Now let’s talk about your sleep, energy and diet. They’re all linked to your gut.',
        ],
      },
    },
    {
      path: '/e12/energy',
      type: 'SelectScreen',
      props: {
        title: 'How is your energy during the day?',
        questionKey: 'energy',
        withZiggie: { gif: 'ziggie-idle' },
        options: [
          { label: 'Low, I feel tired all day', value: 'low' },
          { label: 'Inconsistent, I get slumps', value: 'inconsistent' },
          {
            label: 'High, I feel steady all day',
            value: 'high',
            playback: 'Amazing! We’ll help you fine-tune what’s already working.',
          },
        ],
      },
    },
    {
      path: '/e12/energy-playback',
      type: 'ContentScreen',
      showWhen: { energy: ['low', 'inconsistent'] },
      props: {
        title: 'ZOE members are 4x more likely to report better energy',
        description:
          "Food shapes how steady your energy feels throughout the day. We'll show you how to eat to fuel your gut and keep your energy stable.",
        regularFooter: true,
        topImage: '/img/e10/energy-playback.png',
        squareImage: true,
        topImageAspectRatio: '800 / 605',
        imageObjectFit: 'contain',
        tightTop: true,
        tightFooter: true,
        footerBody: 'Source: ZOE research in Nature Medicine, 2024',
      },
    },
    {
      path: '/e12/sleep-hours',
      type: 'SelectScreen',
      props: {
        title: 'How much sleep do you usually get?',
        questionKey: 'sleep_hours',
        withZiggie: { gif: 'ziggie-idle' },
        options: [
          { label: 'Less than 5 hours', value: 'less_than_5' },
          { label: '5-6 hours', value: '5_6' },
          { label: '7-8 hours', value: '7_8' },
          { label: 'More than 8 hours', value: '8_plus' },
        ],
      },
    },
    {
      path: '/e12/sleep-quality',
      type: 'SelectScreen',
      props: {
        title: 'How is the quality of your sleep?',
        questionKey: 'sleep_quality',
        withZiggie: { gif: 'ziggie-idle' },
        options: [
          {
            label: 'Good, I sleep well',
            value: 'sleep_well',
            playback: 'Sleeping well is a solid base. I’ll help you build on it, gut first.',
          },
          { label: 'I struggle falling asleep', value: 'struggle_falling_asleep' },
          { label: 'I wake up tired', value: 'wake_up_tired' },
          { label: 'I wake during the night', value: 'wake_during_night' },
          { label: 'I get hot flushes', value: 'hot_flushes' },
        ],
      },
    },
    {
      path: '/e12/sleep-playback',
      type: 'ContentScreen',
      showWhen: { sleep_quality: ['struggle_falling_asleep', 'wake_up_tired', 'wake_during_night', 'hot_flushes'] },
      props: {
        title: 'People who follow ZOE are 4x more likely to report better sleep',
        description: 'Poor sleep is frustrating and affects your health, but ZOE is scientifically proven to work.',
        regularFooter: true,
        topImage: '/img/e10/sleep-playback.png',
        squareImage: true,
        topImageAspectRatio: '800 / 602',
        imageObjectFit: 'contain',
        tightTop: true,
        tightFooter: true,
        footerBody: 'Source: ZOE research in Nature Medicine, 2024',
      },
    },

    // ============================================================
    // SECTION 3: Diet quality
    // ============================================================
    {
      path: '/e12/diet-type',
      type: 'MultiSelectScreen',
      props: {
        title: 'Do you follow a specific diet?',
        description: 'Select all that apply',
        questionKey: 'diet_type',
        withZiggie: { gif: 'ziggie-idle' },
        options: [
          { label: 'No, I eat everything', value: 'no', exclusive: true },
          { label: 'Vegetarian', value: 'vegetarian', exclusiveGroup: 'diet_pattern' },
          { label: 'Vegan', value: 'vegan', exclusiveGroup: 'diet_pattern' },
          { label: 'Pescatarian', value: 'pescatarian', exclusiveGroup: 'diet_pattern' },
          { label: 'Gluten-free', value: 'gluten_free' },
          { label: 'Dairy-free', value: 'dairy_free' },
          { label: 'Other', value: 'other' },
        ],
      },
    },
    {
      path: '/e12/diet-quality',
      type: 'SelectScreen',
      props: {
        title: 'How would you rate your diet quality?',
        questionKey: 'diet_quality',
        withZiggie: { gif: 'ziggie-idle' },
        options: [
          {
            label: 'It’s not as healthy as I’d like',
            value: 'poor',
            playback: '29% of people start here. I’ll share options that fit your routine.',
          },
          {
            label: 'It varies day-to-day',
            value: 'average',
            playback: '44% of people say this too. I’ll show you simple changes that stick.',
          },
          {
            label: 'It’s healthy and well-balanced',
            value: 'good',
            playback: 'A balanced diet is a great place to start. Let’s build on it.',
          },
        ],
      },
    },
    {
      path: '/e12/snacker',
      type: 'SelectScreen',
      props: {
        title: 'Are you a snacker?',
        questionKey: 'snacker',
        withZiggie: { gif: 'ziggie-idle' },
        options: [
          {
            label: 'Yes',
            value: 'yes',
            playback: 'You can keep snacking! I’ll use ZOE’s science to help you snack smarter.',
          },
          {
            label: 'No',
            value: 'no',
            playback: 'Meals do the heavy lifting, then. I’ll help you make each one count.',
          },
        ],
      },
    },
    {
      path: '/e12/processed-food-frequency',
      type: 'SelectScreen',
      props: {
        title: 'How often do you eat processed foods?',
        description: 'Examples include shop-bought bread, sweeteners, fizzy drinks, ready meals, and breakfast cereals',
        questionKey: 'processed_food_frequency',
        withZiggie: { gif: 'ziggie-idle' },
        options: [
          {
            label: 'Most meals',
            value: 'most_meals',
            playback: 'They’re designed to be hard to resist. I’ll help you navigate them.',
          },
          {
            label: 'Once a day',
            value: 'once_a_day',
            playback: 'They’re designed to be hard to resist. I’ll show you simple swaps.',
          },
          {
            label: 'A few times a week',
            value: 'few_times_a_week',
            playback: 'You’re already making thoughtful choices. I’ll help you build on them.',
          },
          {
            label: 'Rarely or never',
            value: 'rarely_never',
            playback: 'You’re doing great! On average, 60% of what we eat is processed.',
          },
        ],
      },
    },
    {
      path: '/e12/plant-frequency',
      type: 'SelectScreen',
      props: {
        title: 'How many plants do you eat a day?',
        description: 'Examples include fruits, vegetables, whole grains, nuts, seeds, herbs, and spices',
        questionKey: 'plant_frequency',
        withZiggie: { gif: 'ziggie-idle' },
        options: [
          {
            label: '1–3 plants',
            value: '1_3',
            playback: 'Lots start here. Research recommends 30 a week. We’ll add more together.',
          },
          {
            label: '4–6 plants',
            value: '4_6',
            playback: 'Good mix. ZOE’s research says variety wins. We’ll aim for 30 a week.',
          },
          {
            label: '7+ plants',
            value: '7_plus',
            playback: 'Your gut microbes are eating well. We’ll work on variety and consistency.',
          },
        ],
      },
    },
    {
      path: '/e12/thanks-for-sharing',
      type: 'ZiggieTalkScreen',
      backgroundColor: CREAM,
      props: {
        gif: 'ziggie-praise',
        messages: [
          '<b>Thanks for sharing!</b> I’ll use it to personalise your plan. Next up, I’ve got a few questions about you.',
        ],
      },
    },

    // ============================================================
    // SECTION 4a: About you (no weight loss)
    // ============================================================
    {
      path: '/e12/lifestyle-activity',
      type: 'SelectScreen',
      showWhen: { no_weight_loss_goal: 'true' },
      props: {
        title: 'How active are you?',
        questionKey: 'lifestyle_activity',
        withZiggie: { gif: 'ziggie-idle' },
        options: [
          {
            label: 'Not active - I have limited mobility or cannot exercise',
            value: 'not_active',
            playback: 'Thanks for sharing. I’ll show you how to support your gut with food.',
          },
          {
            label: 'Mostly sedentary - I sit for most of the day',
            value: 'mostly_sedentary',
            playback: '28% of people say this too. Even small bits of movement help your gut.',
          },
          {
            label: 'Lightly active - I move often',
            value: 'lightly_active',
            playback: 'Great! Both your body and your gut benefit from physical activity.',
          },
          {
            label: 'Very active - I have a physical job or exercise regularly',
            value: 'very_active',
            playback: 'Love it! I’ll share science-backed nutrition advice to fuel your activity.',
          },
        ],
      },
    },
    {
      path: '/e12/biological-sex',
      type: 'SelectScreen',
      showWhen: { no_weight_loss_goal: 'true' },
      props: {
        title: 'What sex were you assigned at birth?',
        questionKey: 'biological_sex',
        withZiggie: { gif: 'ziggie-idle' },
        options: [
          { label: 'Male', value: 'male' },
          { label: 'Female', value: 'female' },
        ],
      },
    },
    {
      path: '/e12/height',
      type: 'InputHeightScreen',
      showWhen: { no_weight_loss_goal: 'true' },
      props: {
        title: 'What’s your height?',
        defaultUnit: 'ft',
        withZiggie: { gif: 'ziggie-idle' },
      },
    },
    {
      path: '/e12/current-weight',
      type: 'InputWeightScreen',
      showWhen: { no_weight_loss_goal: 'true' },
      props: {
        title: 'What’s your current weight?',
        defaultWeightUnit: 'kg',
        withZiggie: { gif: 'ziggie-idle' },
      },
    },
    {
      path: '/e12/antibiotics',
      type: 'SelectScreen',
      showWhen: { no_weight_loss_goal: 'true' },
      props: {
        title: 'Have you taken antibiotics in the last three years?',
        questionKey: 'antibiotics',
        withZiggie: { gif: 'ziggie-idle' },
        options: [
          {
            label: 'Yes',
            value: 'true',
            playback: 'Got it. Antibiotics can disrupt your gut, but food can help it recover.',
          },
          { label: 'No', value: 'false', playback: 'Great! Every answer helps me get to know your gut.' },
        ],
      },
    },
    {
      path: '/e12/blood-tests',
      type: 'SelectScreen',
      showWhen: { no_weight_loss_goal: 'true' },
      props: {
        title: 'Have you had any blood tests in the last two years?',
        questionKey: 'blood_tests',
        withZiggie: { gif: 'ziggie-idle' },
        options: [
          {
            label: 'Yes',
            value: 'true',
            playback: 'Handy. Keep those results close, they’ll be useful later.',
          },
          { label: 'No', value: 'false' },
        ],
      },
    },

    // ============================================================
    // SECTION 4b: About you (weight loss)
    // ============================================================
    {
      path: '/e12/wl-biological-sex',
      type: 'SelectScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'What sex were you assigned at birth?',
        questionKey: 'biological_sex',
        withZiggie: { gif: 'ziggie-idle' },
        options: [
          { label: 'Male', value: 'male' },
          { label: 'Female', value: 'female' },
        ],
      },
    },
    {
      path: '/e12/wl-height',
      type: 'InputHeightScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'What’s your height?',
        defaultUnit: 'ft',
        withZiggie: { gif: 'ziggie-idle' },
      },
    },
    {
      path: '/e12/wl-current-weight',
      type: 'InputWeightScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'What’s your current weight?',
        defaultWeightUnit: 'kg',
        withZiggie: { gif: 'ziggie-idle' },
      },
    },
    {
      // Design shows no description under the question.
      path: '/e12/future-weight',
      type: 'InputTargetWeightScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'And what weight would you like to be?',
        descriptionLbs: '',
        descriptionKg: '',
        withZiggie: { gif: 'ziggie-idle' },
      },
    },
    {
      path: '/e12/weight-proven',
      type: 'ContentScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'Better gut health leads to lasting weight loss',
        description:
          'In our clinical trial, those who followed ZOE advice reduced their waistline by over 2 inches and lost over 4 kg, all without counting calories or restricting foods.',
        topImage: '/img/e4/weight-proven.png',
        topImageAspectRatio: '335 / 250',
        titleAboveImage: true,
        imageObjectFit: 'contain',
        tightTop: true,
        tightImage: true,
        tightFooter: true,
        regularFooter: true,
        footerBody: 'Source: ZOE research in Nature Medicine, 2024',
      },
    },
    {
      path: '/e12/wl-lifestyle-activity',
      type: 'SelectScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'How active are you?',
        questionKey: 'lifestyle_activity',
        withZiggie: { gif: 'ziggie-idle' },
        options: [
          {
            label: 'Not active - I have limited mobility or cannot exercise',
            value: 'not_active',
            playback: 'Thanks for sharing. I’ll show you how to support your gut with food.',
          },
          {
            label: 'Mostly sedentary - I sit for most of the day',
            value: 'mostly_sedentary',
            playback: '28% of people say this too. Even small bits of movement help your gut.',
          },
          {
            label: 'Lightly active - I move often',
            value: 'lightly_active',
            playback: 'Great! Both your body and your gut benefit from physical activity.',
          },
          {
            label: 'Very active - I have a physical job or exercise regularly',
            value: 'very_active',
            playback: 'Love it! I’ll share science-backed nutrition advice to fuel your activity.',
          },
        ],
      },
    },
    {
      path: '/e12/wl-glp1s',
      type: 'SelectScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'Are you currently taking GLP-1 weight loss medication?',
        questionKey: 'glp1s',
        withZiggie: { gif: 'ziggie-idle' },
        options: [
          {
            label: 'Yes',
            value: 'true',
            playback: 'Thanks for sharing. The food you eat can help, too. I’ll show you how.',
          },
          { label: 'No', value: 'false' },
        ],
      },
    },
    {
      path: '/e12/wl-antibiotics',
      type: 'SelectScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'Have you taken antibiotics in the last three years?',
        questionKey: 'antibiotics',
        withZiggie: { gif: 'ziggie-idle' },
        options: [
          {
            label: 'Yes',
            value: 'true',
            playback: 'Got it. Antibiotics can disrupt your gut, but food can help it recover.',
          },
          { label: 'No', value: 'false', playback: 'Great! Every answer helps me get to know your gut.' },
        ],
      },
    },
    {
      path: '/e12/wl-blood-tests',
      type: 'SelectScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'Have you had any blood tests in the last two years?',
        questionKey: 'blood_tests',
        withZiggie: { gif: 'ziggie-idle' },
        options: [
          {
            label: 'Yes',
            value: 'true',
            playback: 'Handy. Keep those results close, they’ll be useful later.',
          },
          { label: 'No', value: 'false' },
        ],
      },
    },
    {
      path: '/e12/weight-goal-graph',
      type: 'WeightCalculationScreen',
      backgroundColor: CREAM,
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        projectionGraph: true,
        title: 'Transform your gut health and lose weight with ZOE',
        description:
          'Based on your answers, we predict you’ll lose weight with ZOE, <b>sleep better</b>, <b>have more energy</b>, <b>live longer</b>, and much more.',
      },
    },

    // ============================================================
    // CONVERGENCE: Health summary (end of About you, before the gut quiz)
    // ============================================================
    {
      path: '/e12/health-summary',
      type: 'HealthSummaryScreen',
      hideProgressBar: true,
      backgroundColor: CREAM,
      props: {
        title: 'Here’s what you’ve told me so far.',
        withZiggie: { gif: 'ziggie-idle' },
        regularLabels: true,
        summaryItems: [
          {
            label: 'Sleep duration',
            questionKey: 'sleep_hours',
            icon: 'moon',
            formatAnswer: {
              less_than_5: 'Less than 5 hours',
              '5_6': '5-6 hours',
              '7_8': '7-8 hours',
              '8_plus': 'More than 8 hours',
            },
          },
          {
            label: 'Sleep quality',
            questionKey: 'sleep_quality',
            icon: 'cgm',
            formatAnswer: {
              sleep_well: 'Good',
              struggle_falling_asleep: 'Average',
              wake_up_tired: 'Average',
              wake_during_night: 'Poor',
              hot_flushes: 'Poor',
            },
          },
          {
            label: 'Energy',
            questionKey: 'energy',
            icon: 'flash',
            formatAnswer: {
              low: 'Low',
              inconsistent: 'Inconsistent',
              high: 'High',
            },
          },
          {
            label: 'Lifestyle',
            questionKey: 'lifestyle_activity',
            icon: 'body-fat',
            formatAnswer: {
              not_active: 'Not active',
              mostly_sedentary: 'Sedentary',
              lightly_active: 'Lightly active',
              very_active: 'Very active',
            },
          },
          {
            label: 'Diet type',
            questionKey: 'diet_type',
            icon: 'food-drink',
            formatAnswer: {
              no: 'Omnivore',
              vegetarian: 'Vegetarian',
              vegan: 'Vegan',
              pescatarian: 'Pescatarian',
              gluten_free: 'Gluten-free',
              dairy_free: 'Dairy-free',
              other: 'Other',
            },
          },
          {
            label: 'Diet quality',
            questionKey: 'diet_quality',
            icon: 'generic-food',
            // Square glyph: 16px inside the 24px column matches the other icons.
            iconSize: 16,
            formatAnswer: {
              poor: 'Low',
              average: 'Average',
              good: 'High',
            },
          },
          {
            label: 'Current weight',
            questionKey: 'current_weight',
            icon: 'scales',
            formatFn: 'weightWithUnit' as const,
            showWhen: { has_weight_loss_goal: 'true' },
          },
          {
            label: 'Ideal weight',
            questionKey: 'ideal_weight',
            icon: 'calendar-2',
            formatFn: 'weightWithUnit' as const,
            showWhen: { has_weight_loss_goal: 'true' },
          },
        ],
      },
    },

    // ============================================================
    // SECTION 5: Gut health quiz
    // ============================================================
    {
      path: '/e12/gut-quiz-intro',
      type: 'ZiggieTalkScreen',
      backgroundColor: CREAM,
      props: {
        gif: 'ziggie-idle',
        messages: [
          '<b>Your gut is the key to transforming your health.</b> Let’s find out what you already know to better personalise your plan.',
        ],
      },
    },
    {
      path: '/e12/gut-dna-question',
      type: 'SelectScreen',
      props: {
        title: 'Your gut is more unique than your DNA',
        description: 'True or false?',
        subtitleAboveTitle: true,
        questionKey: 'gut_dna_question',
        options: [
          { label: 'True', value: 'true' },
          { label: 'False', value: 'false' },
        ],
      },
    },
    {
      path: '/e12/gut-dna-answer',
      backgroundColor: CREAM,
      type: 'QuizAnswerScreen',
      props: {
        questionKey: 'gut_dna_question',
        correctAnswer: 'true',
        withZiggie: { correctGif: 'ziggie-praise', incorrectGif: 'ziggie-head-scratch' },
        description:
          'The gut houses <b>trillions of bacteria, all completely unique to you</b>. Even identical twins share just 34% of the same gut bacteria!',
        footer: 'Source: ZOE research in Nature Medicine, 2020',
      },
    },
    {
      path: '/e12/gut-impacts-health-question',
      type: 'SelectScreen',
      props: {
        title: 'Your gut impacts almost every aspect of your health',
        description: 'True or false?',
        subtitleAboveTitle: true,
        questionKey: 'gut_impacts_health_question',
        options: [
          { label: 'True', value: 'true' },
          { label: 'False', value: 'false' },
        ],
      },
    },
    {
      path: '/e12/gut-impacts-health-answer',
      backgroundColor: CREAM,
      type: 'QuizAnswerScreen',
      props: {
        questionKey: 'gut_impacts_health_question',
        correctAnswer: 'true',
        withZiggie: { correctGif: 'ziggie-praise', incorrectGif: 'ziggie-head-scratch' },
        description:
          'Our understanding of gut microbes has transformed in the last decade. We now know that <b>your gut is the foundation of your health.</b>{br}{br}' +
          'Beyond digestion, it regulates metabolism, influences mood, and houses 70% of your immune system.',
        footer: 'Source: Hou et al., 2022 and Wiertsema et al., 2021',
      },
    },
    {
      path: '/e12/gut-change-in-days-question',
      type: 'SelectScreen',
      props: {
        title: 'It takes at least 3 months to change your gut microbiome',
        description: 'True or false?',
        subtitleAboveTitle: true,
        questionKey: 'gut_change_in_days_question',
        options: [
          { label: 'True', value: 'true' },
          { label: 'False', value: 'false' },
        ],
      },
    },
    {
      path: '/e12/gut-change-in-days-answer',
      backgroundColor: CREAM,
      type: 'QuizAnswerScreen',
      props: {
        questionKey: 'gut_change_in_days_question',
        correctAnswer: 'false',
        withZiggie: { correctGif: 'ziggie-praise', incorrectGif: 'ziggie-head-scratch' },
        description:
          '<b>Your gut microbiome starts to change in just days</b>, and these changes will steadily build over the following months with ZOE.{br}{br}' +
          'By choosing foods that suit your unique biology, you’ll harness the power of your gut to reach your health goals.',
        footer: 'Source: Wu et al., 2012',
      },
    },

    // ============================================================
    // SECTION 6: Symptom questionnaire (e7 "Symptom score v2")
    // Reversed scoring: Not at all = 1 ... Extremely = 4.
    // ============================================================
    {
      path: '/e12/symptom-intro',
      type: 'ZiggieTalkScreen',
      backgroundColor: CREAM,
      props: {
        gif: 'ziggie-idle',
        messages: [
          '<b>Now, let’s get your Symptom Severity Score.</b> Based on ZOE’s research from 100k+ people, it’ll show us where to focus, and how much you could improve.',
        ],
      },
    },
    {
      path: '/e12/symptom-gut-and-digestion',
      type: 'MultipleSlidersScreen',
      props: {
        title: 'How much have you been bothered by these symptoms in the past week?',
        sliderOptionValues: ['1', '2', '3', '4'],
        labels: ['Not at all', 'A little', 'Quite a bit', 'Extremely'],
        requiredFieldMissingMessage: 'Please select an option for this symptom.',
        tag: { label: 'Gut and digestion', colorScheme: 'gut_digestion' },
        questions: [
          { question: 'Digestive discomfort', questionKey: 'digestive_discomfort' },
          { question: 'Bloating', questionKey: 'bloating' },
          { question: 'Acid reflux and indigestion', questionKey: 'acid_reflux_and_indigestion' },
          { question: 'Irregular bowel movements', questionKey: 'irregular_bowel_movements' },
        ],
      },
    },
    {
      path: '/e12/symptom-inflammation',
      type: 'MultipleSlidersScreen',
      props: {
        title: 'How much have you been bothered by these symptoms in the past week?',
        sliderOptionValues: ['1', '2', '3', '4'],
        labels: ['Not at all', 'A little', 'Quite a bit', 'Extremely'],
        requiredFieldMissingMessage: 'Please select an option for this symptom.',
        tag: { label: 'Inflammation', colorScheme: 'inflammation' },
        questions: [
          { question: 'Joint and muscular discomfort', questionKey: 'joint_and_muscular_discomfort' },
          {
            question: 'Decreased physical strength or stamina',
            questionKey: 'decreased_physical_strength_or_stamina',
          },
          { question: 'Skin issues', questionKey: 'skin_issues' },
          { question: 'Hair loss', questionKey: 'hair_loss' },
        ],
      },
    },
    {
      path: '/e12/symptom-energy-and-mood',
      type: 'MultipleSlidersScreen',
      props: {
        title: 'How much have you been bothered by these symptoms in the past week?',
        sliderOptionValues: ['1', '2', '3', '4'],
        labels: ['Not at all', 'A little', 'Quite a bit', 'Extremely'],
        requiredFieldMissingMessage: 'Please select an option for this symptom.',
        tag: { label: 'Energy and mood', colorScheme: 'energy_mood' },
        questions: [
          { question: 'Tiredness and fatigue', questionKey: 'tiredness_and_fatigue' },
          { question: 'Poor sleep quality', questionKey: 'poor_sleep_quality' },
          { question: 'Brain fog or trouble concentrating', questionKey: 'brain_fog_or_trouble_concentrating' },
          { question: 'Stress or low mood', questionKey: 'stress_or_low_mood' },
        ],
      },
    },
    {
      path: '/e12/symptom-appetite-and-weight',
      type: 'MultipleSlidersScreen',
      props: {
        title: 'How much have you been bothered by these symptoms in the past week?',
        sliderOptionValues: ['1', '2', '3', '4'],
        labels: ['Not at all', 'A little', 'Quite a bit', 'Extremely'],
        requiredFieldMissingMessage: 'Please select an option for this symptom.',
        tag: { label: 'Appetite and weight', colorScheme: 'appetite_weight' },
        questions: [
          { question: 'Hunger', questionKey: 'hunger' },
          { question: 'Cravings', questionKey: 'cravings' },
          {
            question: 'Weight gain or difficulty managing weight',
            questionKey: 'weight_gain_or_difficulty_managing_weight',
          },
        ],
      },
    },
    {
      // Conditional: women aged 35+ only.
      path: '/e12/symptom-hormonal',
      type: 'MultipleSlidersScreen',
      showWhen: { welcome_age_range: ['35_49', '50_64', '65_plus'], biological_sex: 'female' },
      props: {
        title: 'How much have you been bothered by these symptoms in the past week?',
        sliderOptionValues: ['1', '2', '3', '4'],
        labels: ['Not at all', 'A little', 'Quite a bit', 'Extremely'],
        requiredFieldMissingMessage: 'Please select an option for this symptom.',
        tag: { label: 'Hormonal', colorScheme: 'hormonal' },
        questions: [
          { question: 'Night sweats', questionKey: 'night_sweats' },
          { question: 'Hot flushes', questionKey: 'hot_flushes' },
          { question: 'Irritability', questionKey: 'irritability' },
          { question: 'Loss of interest in sex', questionKey: 'loss_of_interest_in_sex' },
        ],
      },
    },
    {
      path: '/e12/symptom-score-calculating',
      type: 'SymptomScoreCalculatingScreenE7',
      hideProgressBar: true,
      props: {},
    },
    {
      path: '/e12/symptom-score',
      type: 'SymptomScoreResultsScreenE7',
      hideProgressBar: true,
      props: {
        compactKeySymptoms: true,
        withZiggie: {
          gif: 'ziggie-idle',
          messages: {
            '0-20':
              '<b>Even mild symptoms are worth sorting out. </b>Let’s start with the ones that bother you the most.',
            '21-49':
              '<b>You’re dealing with more than you need to. </b>Your plan will start with the symptoms that bother you most.',
            '50-100':
              '<b>That’s a lot to be dealing with.</b> Your plan will start with the symptoms that bother you most.',
          },
        },
      },
    },

    // ============================================================
    // SECTION 7a: Future you — no weight loss (unchanged from e11)
    // ============================================================
    {
      path: '/e12/future-score',
      type: 'FutureScoreScreenE7',
      backgroundColor: CREAM,
      showWhen: { no_weight_loss_goal: 'true' },
      props: { projectionGraph: true },
    },

    // ============================================================
    // SECTION 7b: Future you — weight loss (unchanged from e11)
    // ============================================================
    {
      path: '/e12/future-score-weight-loss',
      type: 'FutureScoreScreenE7',
      backgroundColor: CREAM,
      showWhen: { has_weight_loss_goal: 'true' },
      props: { weightLoss: true, projectionGraph: true },
    },

    // ============================================================
    // SECTION 8: Wrap-up (unchanged from e11)
    // ============================================================
    {
      path: '/e12/email',
      type: 'EmailCaptureScreen',
      props: {
        title: 'Get your personalised plan and free gut health guide',
        topImage: '/img/e4/email-guide.png',
        topImageAspectRatio: '1005 / 471',
        buttonText: 'Continue',
        footerBody:
          "We'll also send you ongoing science and nutrition emails, news and offers.{br}{optOutLink}. See our {privacyPolicy}.",
        showMarketingConsent: true,
        defaultMarketingOptIn: true,
      },
    },
    {
      path: '/e12/results',
      type: 'ResultsLoadingScreen',
      hideProgressBar: true,
      props: {
        title: 'We are building your personalised plan',
        steps: [
          'Analysing your health data',
          'Reviewing your goals',
          'Personalising your insights',
          'Generating your action plan',
        ],
        trustedByText: 'Trusted by over 3 million',
        testimonials: [
          {
            name: 'Alison',
            quote:
              'I’ve noticed a change in my digestion, and I’ve noticed a change in my weight. And that’s all without restricting anything.',
          },
          {
            name: 'Selena',
            quote:
              'I feel more confident about food, food choices and regaining my independence, and I am seeing the difference in my energy.',
          },
          {
            name: 'Patty',
            quote:
              'Admittedly, I have been a sugar addict. I have noticed this week that I am no longer desiring highly processed, high sugar candy!',
          },
          {
            name: 'Kate',
            quote: 'Changed my mindset to focus on whole foods and not calorie content.',
          },
          {
            name: 'Dorna',
            quote:
              'Before Zoe, I think I was feeling lethargic. And now I feel a lot healthier and I’m more energetic. And I have the energy to exercise more go out on longer hikes.',
          },
        ],
        buttonText: 'Discover my plan',
        exitPath: GB_EXIT_PATH,
      },
    },
  ],
}

export default e12
